import React from 'react';
import { Guest, TABLES } from './seatingTypes';
import PrintHonorTable from './PrintHonorTable';
import PrintRegularTable from './PrintRegularTable';

interface PrintViewProps {
  assignments: Map<string, Guest>;
  tableOrder: string[];
  onBack: () => void;
}

const buildCatererIndex = (
  assignments: Map<string, Guest>,
  tableOrder: string[]
): Map<number, number> => {
  const index = new Map<number, number>();
  let counter = 1;
  const allTableKeys = ['honor', ...tableOrder];
  for (const tableKey of allTableKeys) {
    const table = TABLES[tableKey as keyof typeof TABLES];
    if (!table) continue;
    for (let i = 0; i < table.seats; i++) {
      const guest = assignments.get(`${tableKey}-${i}`);
      if (guest && guest.messageForCaterer && guest.message) {
        index.set(guest.id, counter++);
      }
    }
  }
  return index;
};

const PrintView: React.FC<PrintViewProps> = ({ assignments, tableOrder, onBack }) => {
  const catererIndex = buildCatererIndex(assignments, tableOrder);

  const catererGuests: Array<{ guest: Guest; refNum: number; tableName: string; seatNum: number }> = [];
  catererIndex.forEach((refNum, guestId) => {
    const guest = Array.from(assignments.values()).find((g) => g.id === guestId);
    if (guest) {
      const tName = guest.tableAssignment
        ? TABLES[guest.tableAssignment.tableName as keyof typeof TABLES]?.name || guest.tableAssignment.tableName
        : 'Non placé';
      const seatNum = guest.tableAssignment ? guest.tableAssignment.seatIndex + 1 : 0;
      catererGuests.push({ guest, refNum, tableName: tName, seatNum });
    }
  });
  catererGuests.sort((a, b) => a.refNum - b.refNum);

  return (
    <div className="print-view">
      <div className="print-no-print">
        <button className="modern-btn primary" onClick={onBack}>
          Retour au plan de table
        </button>
        <button className="modern-btn" onClick={() => window.print()}>
          Imprimer
        </button>
      </div>

      <div className="print-content">
        <h1 className="print-title">Plan de table - Traiteur</h1>

        <div className="print-legend">
          <span className="print-legend-item"><span className="print-legend-box print-legend-occupied"></span> Occupée</span>
          <span className="print-legend-item"><span className="print-legend-box print-legend-empty"></span> Libre</span>
          <span className="print-legend-item"><span className="print-legend-box print-legend-caterer"></span> Message traiteur (*N)</span>
        </div>

        <div className="print-tables">
          <PrintHonorTable assignments={assignments} catererIndex={catererIndex} />
          <div className="regular-tables">
            {tableOrder.map((key) => (
              <PrintRegularTable key={key} tableKey={key} assignments={assignments} catererIndex={catererIndex} />
            ))}
          </div>
        </div>

        {catererGuests.length > 0 && (
          <div className="print-summary">
            <h2>Récapitulatif traiteur ({catererGuests.length})</h2>
            <table className="print-summary-table">
              <thead>
                <tr>
                  <th>Réf.</th>
                  <th>Nom</th>
                  <th>Table / Place</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {catererGuests.map(({ guest, refNum, tableName, seatNum }) => (
                  <tr key={guest.id}>
                    <td className="print-ref-col">*{refNum}</td>
                    <td>{guest.firstName} {guest.lastName || ''}</td>
                    <td>{tableName} - n°{seatNum}</td>
                    <td className="print-message-col">{guest.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintView;
